import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { firstValueFrom } from 'rxjs';
import { appRoutes } from 'app/app.routes';
import { customprovideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { JwtModule } from "@auth0/angular-jwt";
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS, HttpClient } from "@angular/common/http";
import { environment } from '../environments/environment';
import { CommonService } from './Common/common.service';
import { SubscriptionService } from './Common/subscription.service';
import { PushNotificationsService } from './Common/notification.service';
import { CustomvalidationService } from './Common/Validators/customvalidation.service';
import { TokenInterceptorService } from './token-interceptor.service';
import { LoaderInterceptorService } from './loader-interceptor.service';
import { AuthTokenService } from './core/auth/auth-token.service';
import { catchError, map, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { AngularFireModule } from '@angular/fire/compat';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { Loader } from "@googlemaps/js-api-loader";
import { ReCaptchaV3Provider, initializeAppCheck, provideAppCheck } from '@angular/fire/app-check';

export function tokenGetter() {
  return localStorage.getItem("jwt");
}

//function appInitializer(commonService: CommonService, authTokenService: AuthTokenService) {
//  return () => firstValueFrom(
//    authTokenService.ensureValidAccessToken().pipe(
//      switchMap(() => {
//        // Skip on login/guest when there is no token
//        if (!authTokenService.accessToken) {
//          return of(null);
//        }
//        return commonService.getUserByToken().pipe(catchError(() => of(null)));
//      }),
//      map(() => undefined)
//    )
//  );
//}
function appInitializer(
  commonService: CommonService,
  authTokenService: AuthTokenService
) {
  return () =>
    firstValueFrom(
      authTokenService.ensureValidAccessToken().pipe(
        switchMap((isValid) => {

          // No access token and refresh failed
          if (!isValid || !authTokenService.accessToken) {
            return of(null);
          }

          return commonService.getUserByToken().pipe(
            catchError(() => of(null))
          );
        }),
        map(() => undefined)
      )
    );
}

export const appConfig: ApplicationConfig = {
    providers: [
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    provideDatabase(() => getDatabase()),
    provideFirestore(() => getFirestore()),
    provideAppCheck(() =>
      initializeAppCheck(undefined, {
        provider: new ReCaptchaV3Provider(environment.firebase_app_check_site_key),
        isTokenAutoRefreshEnabled: true
      })
    ),
    provideEnvironmentNgxMask(),
    provideRouter(appRoutes,
      withPreloading(PreloadAllModules),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
    ),
    importProvidersFrom(
      MatNativeDateModule,
      AngularFireModule.initializeApp(environment.firebaseConfig)
    ),
    provideTranslateService({
      lang: 'en',
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: '.json?cacheBuster=${Date.now()}'
      })
    }),
    importProvidersFrom(
      JwtModule.forRoot({
        config: {
          tokenGetter: tokenGetter,
          allowedDomains: ["localhost:44354", "localhost:44422", environment.bucket_name + ".s3.amazonaws.com", "fieldstarr-development.s3.amazonaws.com"],
          disallowedRoutes: []
        }
      }),
      NgxDaterangepickerMd.forRoot(),
    ),
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: 'AIzaSyBqBjL739kbZLHEpIuobTNuNaCBXESlftE',
        libraries: ['places']
      })
    },
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptorService, multi: true },
    { provide: APP_INITIALIZER, useFactory: appInitializer, multi: true, deps: [CommonService, AuthTokenService] },
    CommonService,
    SubscriptionService,
    PushNotificationsService,
    CustomvalidationService,
  
        // Material Date Adapter
        {
            provide : DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide : MAT_DATE_FORMATS,
            useValue: {
                parse  : {
                    dateInput: 'D',
                },
                display: {
                    dateInput         : 'DDD',
                    monthYearLabel    : 'LLL yyyy',
                    dateA11yLabel     : 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        // Transloco Config
      
        // Fuse
        customprovideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay   : 0,
                services: mockApiServices,
            },
            fuse   : {
                layout: 'classic',
                scheme : 'light',
                screens: {
                  sm: '600px',
                  md: '1280px',
                  lg: '1440px',
                  xl: '1840px',
                },
                theme  : 'theme-default',
                themes : [
                    {
                        id  : 'theme-default',
                        name: 'Default',
                    },
                    {
                        id  : 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id  : 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id  : 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id  : 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id  : 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        })
    ],
};
